import { z } from 'zod';

import { unitProfileTemplateKeyValues } from '../unit-profiles/unit-profiles.dto.js';
import {
  holdingUnitAvailabilityReasonValues,
  holdingUnitAdjustmentTypeValues,
  holdingUnitContainerKindValues,
  holdingUnitIdentityModeValues,
  holdingUnitLifecycleStatusValues,
  holdingUnitProductKindValues,
  holdingUnitTypeValues,
  holdingUnitLoadSessionGroupClaimStatusValues,
  holdingUnitLoadSessionMeasuredDiscrepancyActionValues,
  holdingUnitLoadSessionMeasuredDiscrepancyStateValues,
  holdingUnitLoadSessionMatchCandidateClassificationValues,
  holdingUnitLoadSessionItemSelectionMethodValues,
  holdingUnitLoadSessionStatusValues,
  holdingUnitMeasuredDiscrepancyCommittedWeightEventTypeValues,
  holdingUnitReservationTargetTypeValues,
  holdingUnitWeightInputModeValues,
  type HoldingUnitAssistedMatchCandidate,
  type HoldingUnitCapabilities,
  type HoldingUnitLoadSessionDiscrepancyReview,
  type HoldingUnitLoadSessionDiscrepancyReviewItem,
  type HoldingUnitIdentifier,
  type HoldingUnitInventorySummaryItem,
  type HoldingUnitLoadSessionMatchCandidate,
  type HoldingUnitLoadSessionGroupTotals,
  type HoldingUnitLoadSessionItem,
  type HoldingUnitLoadSession,
  type HoldingUnitLoadSessionMaterialGroup,
  type HoldingUnitReservationState,
  type HoldingUnitReservationTarget,
  type HoldingUnitWeightBreakdown,
  type HoldingUnitWeightDelta,
  type HoldingUnitSnapshot,
} from './holding-units.dto.js';

const timestampSchema = z.iso.datetime();

export const HoldingUnitLifecycleStatusSchema = z.enum(holdingUnitLifecycleStatusValues);
export const HoldingUnitAvailabilityReasonSchema = z.enum(holdingUnitAvailabilityReasonValues);
export const HoldingUnitTypeSchema = z.enum(holdingUnitTypeValues);
export const HoldingUnitIdentityModeSchema = z.enum(holdingUnitIdentityModeValues);
export const HoldingUnitContainerKindSchema = z.enum(holdingUnitContainerKindValues);
export const HoldingUnitProductKindSchema = z.enum(holdingUnitProductKindValues);
export const UnitProfileTemplateKeySnapshotSchema = z.enum(unitProfileTemplateKeyValues);
export const HoldingUnitWeightInputModeSchema = z.enum(holdingUnitWeightInputModeValues);
export const HoldingUnitAdjustmentTypeSchema = z.enum(holdingUnitAdjustmentTypeValues);
export const HoldingUnitLoadSessionStatusSchema = z.enum(holdingUnitLoadSessionStatusValues);
export const HoldingUnitLoadSessionGroupClaimStatusSchema = z.enum(
  holdingUnitLoadSessionGroupClaimStatusValues,
);
export const HoldingUnitLoadSessionMeasuredDiscrepancyActionSchema = z.enum(
  holdingUnitLoadSessionMeasuredDiscrepancyActionValues,
);
export const HoldingUnitLoadSessionMeasuredDiscrepancyStateSchema = z.enum(
  holdingUnitLoadSessionMeasuredDiscrepancyStateValues,
);
export const HoldingUnitLoadSessionItemSelectionMethodSchema = z.enum(
  holdingUnitLoadSessionItemSelectionMethodValues,
);
export const HoldingUnitMeasuredDiscrepancyCommittedWeightEventTypeSchema = z.enum(
  holdingUnitMeasuredDiscrepancyCommittedWeightEventTypeValues,
);
export const HoldingUnitLoadSessionMatchCandidateClassificationSchema = z.enum(
  holdingUnitLoadSessionMatchCandidateClassificationValues,
);
export const HoldingUnitReservationTargetTypeSchema = z.enum(
  holdingUnitReservationTargetTypeValues,
);

const identifierTypeSchema = z.string().trim().min(1).max(32);
const identifierValueSchema = z.string().trim().min(1).max(64);

export const HoldingUnitIdentifierSchema: z.ZodType<HoldingUnitIdentifier> = z.object({
  type: identifierTypeSchema,
  value: identifierValueSchema,
});

export const HoldingUnitCapabilitiesSchema: z.ZodType<HoldingUnitCapabilities> = z.object({
  canReceiveIntake: z.boolean(),
  canReceiveTransfer: z.boolean(),
  canTransferOut: z.boolean(),
  canAdjust: z.boolean(),
  canReady: z.boolean(),
  canShip: z.boolean(),
  canRetire: z.boolean(),
  canBuildProduct: z.boolean(),
  canHoldLeftover: z.boolean(),
  canReopen: z.boolean(),
  canDisassemble: z.boolean(),
});

export const HoldingUnitSnapshotSchema: z.ZodType<HoldingUnitSnapshot> = z.object({
  id: z.uuid(),
  companyId: z.uuid(),
  storeId: z.uuid(),
  locationId: z.uuid(),
  yardLocationId: z.uuid().nullable(),
  fixedIdAssetId: z.uuid().nullable().optional(),
  unitProfileId: z.uuid().nullable().optional(),
  unitProfileRevision: z.number().int().min(1).nullable().optional(),
  unitProfileDisplayName: z.string().trim().min(1).max(120).nullable().optional(),
  unitProfileTemplateKey: UnitProfileTemplateKeySnapshotSchema.nullable().optional(),
  capabilityPolicy: HoldingUnitCapabilitiesSchema.nullable().optional(),
  unitType: HoldingUnitTypeSchema,
  identityMode: HoldingUnitIdentityModeSchema,
  containerKind: HoldingUnitContainerKindSchema,
  productKind: HoldingUnitProductKindSchema,
  lifecycleStatus: HoldingUnitLifecycleStatusSchema,
  capabilities: HoldingUnitCapabilitiesSchema,
  materialId: z.uuid().nullable(),
  identifier: HoldingUnitIdentifierSchema.nullable(),
  identifierType: identifierTypeSchema.nullable().optional(),
  tareLb: z.number().nonnegative().nullable(),
  tareSuggestedLb: z.number().nonnegative().nullable().optional(),
  grossLb: z.number().nonnegative().nullable(),
  netLb: z.number().min(0),
  normalizedPhysicalId: z.string().trim().min(1).nullable().optional(),
  normalizedScannableIdentifier: z.string().trim().min(1).nullable().optional(),
  version: z.number().int().nonnegative(),
  lastEventId: z.uuid(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});

export const HoldingUnitReservationTargetBaseSchema = z.object({
  reservationType: HoldingUnitReservationTargetTypeSchema,
  loadSessionId: z.uuid().nullable().optional(),
  shipmentId: z.uuid().nullable().optional(),
});

export const HoldingUnitReservationTargetSchema: z.ZodType<HoldingUnitReservationTarget> =
  HoldingUnitReservationTargetBaseSchema.superRefine((value, ctx) => {
    const hasLoadSession = Boolean(value.loadSessionId);
    const hasShipment = Boolean(value.shipmentId);

    if (value.reservationType === 'loadSession') {
      if (!hasLoadSession) {
        ctx.addIssue({
          code: 'custom',
          message: 'loadSessionId is required for loadSession reservations.',
          path: ['loadSessionId'],
        });
      }
      if (hasShipment) {
        ctx.addIssue({
          code: 'custom',
          message: 'shipmentId must be empty for loadSession reservations.',
          path: ['shipmentId'],
        });
      }
      return;
    }

    if (!hasShipment) {
      ctx.addIssue({
        code: 'custom',
        message: 'shipmentId is required for shipment reservations.',
        path: ['shipmentId'],
      });
    }
    if (hasLoadSession) {
      ctx.addIssue({
        code: 'custom',
        message: 'loadSessionId must be empty for shipment reservations.',
        path: ['loadSessionId'],
      });
    }
  });

export const HoldingUnitReservationStateSchema: z.ZodType<HoldingUnitReservationState> =
  HoldingUnitReservationTargetBaseSchema.extend({
    id: z.uuid(),
    companyId: z.uuid(),
    unitId: z.uuid(),
    storeId: z.uuid(),
    locationId: z.uuid(),
    reservedAt: timestampSchema,
    reservedByUserId: z.uuid().nullable(),
  }).superRefine((value, ctx) => {
    const hasLoadSession = Boolean(value.loadSessionId);
    const hasShipment = Boolean(value.shipmentId);

    if (value.reservationType === 'loadSession') {
      if (!hasLoadSession) {
        ctx.addIssue({
          code: 'custom',
          message: 'loadSessionId is required for loadSession reservations.',
          path: ['loadSessionId'],
        });
      }
      if (hasShipment) {
        ctx.addIssue({
          code: 'custom',
          message: 'shipmentId must be empty for loadSession reservations.',
          path: ['shipmentId'],
        });
      }
      return;
    }

    if (!hasShipment) {
      ctx.addIssue({
        code: 'custom',
        message: 'shipmentId is required for shipment reservations.',
        path: ['shipmentId'],
      });
    }
    if (hasLoadSession) {
      ctx.addIssue({
        code: 'custom',
        message: 'loadSessionId must be empty for shipment reservations.',
        path: ['loadSessionId'],
      });
    }
  });

export const HoldingUnitLoadSessionSchema: z.ZodType<HoldingUnitLoadSession> = z
  .object({
    id: z.uuid(),
    companyId: z.uuid(),
    storeId: z.uuid(),
    locationId: z.uuid(),
    stationId: z.uuid(),
    status: HoldingUnitLoadSessionStatusSchema,
    sessionName: z.string().trim().min(1).max(80).nullable(),
    toleranceProfileKey: z.string().trim().min(1).max(50).nullable(),
    createdByUserId: z.uuid().nullable(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
    version: z.number().int().nonnegative(),
  })
  .strict();

export const HoldingUnitLoadSessionMaterialGroupSchema: z.ZodType<HoldingUnitLoadSessionMaterialGroup> =
  z
    .object({
      id: z.uuid(),
      loadSessionId: z.uuid(),
      companyId: z.uuid(),
      materialId: z.uuid(),
      autoPickEnabled: z.boolean(),
      toleranceProfileKey: z.string().trim().min(1).max(50).nullable(),
      yardLocationIds: z.array(z.uuid()).nullable(),
      createdAt: timestampSchema,
      updatedAt: timestampSchema,
      createdByUserId: z.uuid().nullable(),
    })
    .strict();

export const HoldingUnitWeightBreakdownSchema: z.ZodType<HoldingUnitWeightBreakdown> = z.object({
  grossLb: z.number().min(0).nullable(),
  tareLb: z.number().min(0).nullable(),
  netLb: z.number().min(0).nullable(),
});

export const HoldingUnitWeightDeltaSchema: z.ZodType<HoldingUnitWeightDelta> = z.object({
  grossLb: z.number().nullable(),
  tareLb: z.number().nullable(),
  netLb: z.number().nullable(),
});

export const HoldingUnitLoadSessionItemSchema: z.ZodType<HoldingUnitLoadSessionItem> = z.object({
  id: z.uuid(),
  loadSessionId: z.uuid(),
  groupId: z.uuid(),
  companyId: z.uuid(),
  unitId: z.uuid(),
  selectionMethod: HoldingUnitLoadSessionItemSelectionMethodSchema,
  snapshotNetLbAtSelection: z.number().min(0),
  snapshotGrossLbAtSelection: z.number().min(0).nullable(),
  snapshotTareLbAtSelection: z.number().min(0).nullable(),
  loadMeasuredGrossLb: z.number().min(0).nullable(),
  loadMeasuredNetLb: z.number().min(0).nullable(),
  deltaLossLb: z.number().nullable(),
  toleranceProfileKey: z.string().trim().min(1).max(50).nullable(),
  capturedAt: timestampSchema,
  capturedByUserId: z.uuid().nullable(),
  wasAutoPicked: z.boolean(),
  measuredDiscrepancyAction: HoldingUnitLoadSessionMeasuredDiscrepancyActionSchema.nullable(),
  measuredDiscrepancyResolvedAt: timestampSchema.nullable(),
  measuredDiscrepancyResolvedByUserId: z.uuid().nullable(),
  measuredDiscrepancyCommittedWeightEventId: z.uuid().nullable(),
  measuredDiscrepancyCommittedWeightEventType:
    HoldingUnitMeasuredDiscrepancyCommittedWeightEventTypeSchema.nullable(),
  removedAt: timestampSchema.nullable(),
  removedByUserId: z.uuid().nullable(),
});

export const HoldingUnitLoadSessionDiscrepancyReviewItemSchema: z.ZodType<HoldingUnitLoadSessionDiscrepancyReviewItem> =
  z.object({
    itemId: z.uuid(),
    unitId: z.uuid(),
    firstWeigh: HoldingUnitWeightBreakdownSchema,
    reweighAtLoading: HoldingUnitWeightBreakdownSchema,
    difference: HoldingUnitWeightDeltaSchema,
    officialForShipment: HoldingUnitWeightBreakdownSchema,
    state: HoldingUnitLoadSessionMeasuredDiscrepancyStateSchema,
    canCommit: z.boolean(),
    commitBlockedReason: z.string().trim().min(1).nullable(),
    resolvedAt: timestampSchema.nullable(),
    resolvedByUserId: z.uuid().nullable(),
  });

export const HoldingUnitLoadSessionDiscrepancyReviewSchema: z.ZodType<HoldingUnitLoadSessionDiscrepancyReview> =
  z.object({
    unresolvedCount: z.number().int().nonnegative(),
    items: z.array(HoldingUnitLoadSessionDiscrepancyReviewItemSchema),
  });

export const HoldingUnitLoadSessionGroupTotalsSchema: z.ZodType<HoldingUnitLoadSessionGroupTotals> =
  z.object({
    groupId: z.uuid(),
    unitCount: z.number().int().nonnegative(),
    firstWeighTotal: HoldingUnitWeightBreakdownSchema,
    reweighTotal: HoldingUnitWeightBreakdownSchema,
    differenceTotal: HoldingUnitWeightDeltaSchema,
    officialForShipment: HoldingUnitWeightBreakdownSchema,
  });

export const HoldingUnitInventorySummaryItemSchema: z.ZodType<HoldingUnitInventorySummaryItem> =
  z.object({
    materialId: z.uuid().nullable(),
    lifecycleStatus: HoldingUnitLifecycleStatusSchema,
    unitCount: z.number().int().min(0),
    totalNetLb: z.number().min(0),
  });

export const HoldingUnitAssistedMatchCandidateSchema: z.ZodType<HoldingUnitAssistedMatchCandidate> =
  z.object({
    unitId: z.uuid(),
    lifecycleStatus: HoldingUnitLifecycleStatusSchema,
    materialId: z.uuid().nullable(),
    grossLb: z.number().nonnegative().nullable(),
    tareLb: z.number().nonnegative().nullable(),
    netLb: z.number().min(0),
    deltaNetLb: z.number(),
    exactMatch: z.boolean(),
  });

export const HoldingUnitLoadSessionMatchCandidateSchema: z.ZodType<HoldingUnitLoadSessionMatchCandidate> =
  z.object({
    unitId: z.uuid(),
    expectedGrossLb: z.number().nonnegative(),
    expectedNetLb: z.number().min(0),
    tareLb: z.number().nonnegative(),
    deltaGrossLb: z.number(),
    classification: HoldingUnitLoadSessionMatchCandidateClassificationSchema,
  });
